import type { AppLocale } from './locale';

export type TooltipCopy = {
  /** App / sidebar */
  closeMenuAndSheetList: string;
  openMenuAndSheetList: string;
  reload: string;
  createNewSheet: string;
  importSheet: string;
  editSheetTitle: string;
  selectSheet: string;
  cloneSheet: string;
  exportAndShareSheet: string;
  deleteSheet: string;
  deleteAllGroupsAndPouches: string;
  hideGrid: string;
  showGrid: string;
  hideTooltipGuide: string;
  showTooltipGuide: string;
  openQuickHelp: string;
  openInstruction: string;
  openAppsSite: string;
  unlockTotalAndDisableBalancing: string;
  closeHistoryAndTable: string;
  openHistoryAndTable: string;
  commitTitle: string;
  cancel: string;
  /** タイトル・名前編集の Esc（キャンセルではなく破棄） */
  discard: string;

  /** Pouch */
  editPouchValue: string;
  editPouchTitle: string;
  deletePouch: string;
  deletePouchNotSaved: string;
  splitPouch: string;
  createPouch: string;
  dragMovePouch: string;
  clickEditPouchTitle: string;
  clickEditPouchValue: string;
  toggleSign: string;
  valuePlus: string;
  valueMinus: string;
  valuePlusPlus: string;
  valueMinusMinus: string;
  multiPlus: string;
  multiMinus: string;
  multiPlusPlus: string;
  multiMinusMinus: string;
  roundPlus: string;
  roundMinus: string;
  resetToTimes1: string;
  resetQty: string;
  resetQtyAndRounding: string;
  resetRounding: string;
  /** 数式 [**] or [/][/]: 数量を値に反映（×/÷ 表示でキーが異なる） */
  reflectQtyToValue: string;
  swapValueAndMulti: string;
  enableBalancingToCurrentTotal: string;
  disableBalancing: string;
  switchBalancingToThisPouch: string;
  balancingSameAsBpButton: string;
  balancingSameAsThisButton: string;
  longPressDragClonePouch: string;
  clonePouchDrag: string;

  /** Group */
  editGroupTitle: string;
  deleteGroup: string;
  deleteGroupThisFrame: string;
  deleteGroupProgressive: string;
  deleteGroupAll: string;
  deleteGroupGroups: string;
  deleteGroupInnerGroupsAndPouches: string;
  deleteGroupInnerGroups: string;
  dragMoveGroup: string;
  dragResizeGroup: string;
  dragMoveGroupFrameOnly: string;
  longPressDragCloneGroup: string;
  longPressDragCloneGroupFrameOnly: string;
  cloneGroupDrag: string;
  cloneGroupFrameOnlyDrag: string;
  exportSummaryText: string;
  toggleSignAllPouchesInGroup: string;
  addGroupTotalPouch: string;
  mergeGtpToValues: string;
  mergeGtpToMultipliers: string;
  mergeGtpToMultipliersLongPress: string;
  enableMute: string;

  /** Layer */
  switchLayer: string;
  previousNextLayer: string;
  addLayerEditName: string;
  deleteLayerPressTwice: string;
  selectLayer: string;
  editLayerName: string;
  commitName: string;
  nextLayerEditName: string;
  previousLayerEditName: string;
  addLayer: string;
  deleteLayer: string;

  /** Table */
  exportWholeTableToExcel: string;
  dragSelectCells: string;

  /** History */
  undo: string;
  deleteCurrentHistoryAndUndo: string;
  slideShowReverse: string;
  redo: string;
  deleteCurrentHistoryAndRedo: string;
  slideShowForward: string;
  addSnapshot: string;
  editSnapshotTitle: string;
  restoreSnapshot: string;
  overwriteSnapshot: string;
  deleteSnapshot: string;

  /** Legends */
  editColorLabel: string;

  /** Active sheet */
  doubleClickCreatePouch: string;
  dragCreateGroup: string;
};

const TOOLTIP_COPY: Record<AppLocale, TooltipCopy> = {
  en: {
    closeMenuAndSheetList: 'Close Menu & Sheet List',
    openMenuAndSheetList: 'Open Menu & Sheet List',
    reload: 'Reload',
    createNewSheet: 'Create New Sheet',
    importSheet: 'Import Sheet',
    editSheetTitle: 'Edit Sheet Title',
    selectSheet: 'Select Sheet',
    cloneSheet: 'Clone Sheet',
    exportAndShareSheet: 'Export & Share Sheet',
    deleteSheet: 'Delete Sheet',
    deleteAllGroupsAndPouches: 'Delete All Groups and Pouches',
    hideGrid: 'Hide Grid',
    showGrid: 'Show Grid',
    hideTooltipGuide: 'Hide Tooltip Guide',
    showTooltipGuide: 'Show Tooltip Guide',
    openQuickHelp: 'Open Quick Help (PDF)',
    openInstruction: 'Open Instruction (PDF)',
    openAppsSite: 'Open Apps Site (External)',
    unlockTotalAndDisableBalancing: 'Unlock Total & Disable Balancing',
    closeHistoryAndTable: 'Close History & Table',
    openHistoryAndTable: 'Open History & Table',
    commitTitle: 'Commit Title',
    cancel: 'Cancel',
    discard: 'Discard',
    editPouchValue: 'Edit Pouch Value',
    editPouchTitle: 'Edit Pouch Title',
    deletePouch: 'Delete Pouch',
    deletePouchNotSaved: 'Delete Pouch (not saved to history)',
    splitPouch: 'Split Pouch',
    createPouch: 'Create Pouch',
    dragMovePouch: 'Move Pouch',
    clickEditPouchTitle: 'Edit Pouch Title',
    clickEditPouchValue: 'Edit Pouch Value',
    toggleSign: 'Toggle +/-',
    valuePlus: 'Value +',
    valueMinus: 'Value −',
    valuePlusPlus: 'Value ++',
    valueMinusMinus: 'Value −−',
    multiPlus: 'Qty +',
    multiMinus: 'Qty −',
    multiPlusPlus: 'Qty ++',
    multiMinusMinus: 'Qty −−',
    roundPlus: 'Round +',
    roundMinus: 'Round −',
    resetToTimes1: 'Reset to ×1',
    resetQty: 'Reset Qty',
    resetQtyAndRounding: 'Reset Qty & Rounding',
    resetRounding: 'Reset Rounding',
    reflectQtyToValue: 'Reflect Qty into Value',
    swapValueAndMulti: 'Swap Value & Qty',
    enableBalancingToCurrentTotal: 'Enable Balancing to Current Total',
    disableBalancing: 'Disable Balancing',
    switchBalancingToThisPouch: 'Switch Balancing to This Pouch',
    balancingSameAsBpButton: 'Balancing (same as BP button)',
    balancingSameAsThisButton: 'Balancing (same as this button)',
    longPressDragClonePouch: 'Clone Pouch',
    clonePouchDrag: 'Clone Pouch',
    editGroupTitle: 'Edit Group Title',
    deleteGroup: 'Delete Group',
    deleteGroupThisFrame: 'Delete Group (this frame)',
    deleteGroupProgressive: 'Delete Group (progressive)',
    deleteGroupAll: 'Delete Group (all)',
    deleteGroupGroups: 'Delete Group (groups)',
    deleteGroupInnerGroupsAndPouches: 'Delete Group (inner groups & pouches)',
    deleteGroupInnerGroups: 'Delete Group (inner groups)',
    dragMoveGroup: 'Move Group',
    dragResizeGroup: 'Resize Group',
    dragMoveGroupFrameOnly: 'Move Group Frame Only',
    longPressDragCloneGroup: 'Clone Group',
    longPressDragCloneGroupFrameOnly: 'Clone Group Frame Only',
    cloneGroupDrag: 'Clone Group',
    cloneGroupFrameOnlyDrag: 'Clone Group Frame Only',
    exportSummaryText: 'Export Summary Text',
    toggleSignAllPouchesInGroup: 'Toggle +/- for All Pouches in Group',
    addGroupTotalPouch: 'Add Group Total Pouch',
    mergeGtpToValues: 'Merge Group Total Pouch to Values',
    mergeGtpToMultipliers: 'Merge Group Total Pouch to Qty',
    mergeGtpToMultipliersLongPress: 'Merge Group Total Pouch to Qty',
    enableMute: 'Enable Mute',
    switchLayer: 'Switch Layer',
    previousNextLayer: 'Previous / Next Layer',
    addLayerEditName: 'Add Layer (edit name)',
    deleteLayerPressTwice: 'Delete Layer (press twice)',
    selectLayer: 'Select Layer',
    editLayerName: 'Edit Layer Name',
    commitName: 'Commit Name',
    nextLayerEditName: 'Next Layer (edit name)',
    previousLayerEditName: 'Previous Layer (edit name)',
    addLayer: 'Add Layer',
    deleteLayer: 'Delete Layer',
    exportWholeTableToExcel: 'Export Whole Table to Excel',
    dragSelectCells: 'Select Cells',
    undo: 'Undo',
    deleteCurrentHistoryAndUndo: 'Delete Current History & Undo',
    slideShowReverse: 'Slide Show (Reverse)',
    redo: 'Redo',
    deleteCurrentHistoryAndRedo: 'Delete Current History & Redo',
    slideShowForward: 'Slide Show (Forward)',
    addSnapshot: 'Add Snapshot',
    editSnapshotTitle: 'Edit Snapshot Title',
    restoreSnapshot: 'Restore Snapshot',
    overwriteSnapshot: 'Overwrite Snapshot',
    deleteSnapshot: 'Delete Snapshot',
    editColorLabel: 'Edit Color Label',
    doubleClickCreatePouch: 'Create Pouch',
    dragCreateGroup: 'Create Group',
  },
  ja: {
    closeMenuAndSheetList: 'メニューとシート一覧を閉じる',
    openMenuAndSheetList: 'メニューとシート一覧を開く',
    reload: '再読み込み',
    createNewSheet: '新しいシートを作成',
    importSheet: 'シートをインポート',
    editSheetTitle: 'シート名を編集',
    selectSheet: 'シートを選択',
    cloneSheet: 'シートを複製',
    exportAndShareSheet: 'シートをエクスポート・共有',
    deleteSheet: 'シートを削除',
    deleteAllGroupsAndPouches: 'シート内の全グループ・パウチを削除',
    hideGrid: 'グリッドを非表示',
    showGrid: 'グリッドを表示',
    hideTooltipGuide: '操作ガイドを非表示',
    showTooltipGuide: '操作ガイドを表示',
    openQuickHelp: 'クイックヘルプを開く（PDF）',
    openInstruction: '説明書を開く（PDF）',
    openAppsSite: 'アプリサイトを開く（外部）',
    unlockTotalAndDisableBalancing: '合計のロック解除と帳尻パウチの無効化',
    closeHistoryAndTable: '履歴とテーブルを閉じる',
    openHistoryAndTable: '履歴とテーブルを開く',
    commitTitle: '名前を確定',
    cancel: 'キャンセル',
    discard: '破棄',
    editPouchValue: 'パウチの数値を編集',
    editPouchTitle: 'パウチ名を編集',
    deletePouch: 'パウチを削除',
    deletePouchNotSaved: 'パウチを削除（履歴に保存されません）',
    splitPouch: 'パウチを分解',
    createPouch: 'パウチを作成',
    dragMovePouch: 'パウチを移動',
    clickEditPouchTitle: 'パウチ名を編集',
    clickEditPouchValue: 'パウチの数値を編集',
    toggleSign: '+/- を切り替え',
    valuePlus: '数値 +',
    valueMinus: '数値 −',
    valuePlusPlus: '数値 ++',
    valueMinusMinus: '数値 −−',
    multiPlus: '数量 +',
    multiMinus: '数量 −',
    multiPlusPlus: '数量 ++',
    multiMinusMinus: '数量 −−',
    roundPlus: '丸め +',
    roundMinus: '丸め −',
    resetToTimes1: '×1 にリセット',
    resetQty: '数量をリセット',
    resetQtyAndRounding: '数量と丸めをリセット',
    resetRounding: '丸めをリセット',
    reflectQtyToValue: '数量を値に反映',
    swapValueAndMulti: '数値と数量を入れ替え',
    enableBalancingToCurrentTotal: '現在の合計で帳尻を有効化',
    disableBalancing: '帳尻を無効化',
    switchBalancingToThisPouch: 'このパウチに帳尻を切り替え',
    balancingSameAsBpButton: '帳尻（BP ボタンと同じ）',
    balancingSameAsThisButton: '帳尻（このボタンと同じ）',
    longPressDragClonePouch: 'パウチを複製',
    clonePouchDrag: 'パウチを複製',
    editGroupTitle: 'グループ名を編集',
    deleteGroup: 'グループを削除',
    deleteGroupThisFrame: 'グループを削除（この枠）',
    deleteGroupProgressive: 'グループを削除（段階的）',
    deleteGroupAll: 'グループを削除（すべて）',
    deleteGroupGroups: 'グループを削除（グループ）',
    deleteGroupInnerGroupsAndPouches: 'グループを削除（内側のグループとパウチ）',
    deleteGroupInnerGroups: 'グループを削除（内側のグループ）',
    dragMoveGroup: 'グループを移動',
    dragResizeGroup: 'グループをリサイズ',
    dragMoveGroupFrameOnly: 'グループ枠のみ移動',
    longPressDragCloneGroup: 'グループを複製',
    longPressDragCloneGroupFrameOnly: 'グループ枠のみ複製',
    cloneGroupDrag: 'グループを複製',
    cloneGroupFrameOnlyDrag: 'グループ枠のみ複製',
    exportSummaryText: 'サマリーテキストをエクスポート',
    toggleSignAllPouchesInGroup: 'グループ内のすべてのパウチの +/- を切り替え',
    addGroupTotalPouch: 'グループ合計パウチを追加',
    mergeGtpToValues: 'グループ合計パウチを数値にマージ',
    mergeGtpToMultipliers: 'グループ合計パウチを数量にマージ',
    mergeGtpToMultipliersLongPress: 'グループ合計パウチを数量にマージ',
    enableMute: 'グループをミュート',
    switchLayer: 'レイヤーを切り替え',
    previousNextLayer: '前 / 次のレイヤー',
    addLayerEditName: 'レイヤーを追加（名前を編集）',
    deleteLayerPressTwice: 'レイヤーを削除（2回押す）',
    selectLayer: 'レイヤーを選択',
    editLayerName: 'レイヤー名を編集',
    commitName: '名前を確定',
    nextLayerEditName: '次のレイヤー（名前を編集）',
    previousLayerEditName: '前のレイヤー（名前を編集）',
    addLayer: 'レイヤーを追加',
    deleteLayer: 'レイヤーを削除',
    exportWholeTableToExcel: 'テーブル全体を Excel にエクスポート',
    dragSelectCells: 'セルを選択',
    undo: '元に戻す',
    deleteCurrentHistoryAndUndo: '現在の履歴を削除して元に戻す',
    slideShowReverse: 'スライドショー（逆再生）',
    redo: 'やり直す',
    deleteCurrentHistoryAndRedo: '現在の履歴を削除してやり直す',
    slideShowForward: 'スライドショー（順再生）',
    addSnapshot: 'スナップショットを追加',
    editSnapshotTitle: 'スナップショット名を編集',
    restoreSnapshot: 'スナップショットを復元',
    overwriteSnapshot: 'スナップショットを上書き',
    deleteSnapshot: 'スナップショットを削除',
    editColorLabel: '色ラベルを編集',
    doubleClickCreatePouch: 'パウチを作成',
    dragCreateGroup: 'グループを作成',
  },
  zh: {
    closeMenuAndSheetList: '关闭菜单和工作表列表',
    openMenuAndSheetList: '打开菜单和工作表列表',
    reload: '重新加载',
    createNewSheet: '创建新工作表',
    importSheet: '导入工作表',
    editSheetTitle: '编辑工作表名称',
    selectSheet: '选择工作表',
    cloneSheet: '复制工作表',
    exportAndShareSheet: '导出并分享工作表',
    deleteSheet: '删除工作表',
    deleteAllGroupsAndPouches: '删除所有分组和小袋',
    hideGrid: '隐藏网格',
    showGrid: '显示网格',
    hideTooltipGuide: '隐藏操作指南',
    showTooltipGuide: '显示操作指南',
    openQuickHelp: '打开快速帮助（PDF）',
    openInstruction: '打开说明（PDF）',
    openAppsSite: '打开应用网站（外部）',
    unlockTotalAndDisableBalancing: '解锁合计并禁用平衡小袋',
    closeHistoryAndTable: '关闭历史记录和表格',
    openHistoryAndTable: '打开历史记录和表格',
    commitTitle: '确认标题',
    cancel: '取消',
    discard: '丢弃',
    editPouchValue: '编辑小袋数值',
    editPouchTitle: '编辑袋名',
    deletePouch: '删除小袋',
    deletePouchNotSaved: '删除小袋（不保存到历史记录）',
    splitPouch: '拆分小袋',
    createPouch: '创建小袋',
    dragMovePouch: '移动小袋',
    clickEditPouchTitle: '编辑袋名',
    clickEditPouchValue: '编辑小袋数值',
    toggleSign: '切换 +/-',
    valuePlus: '数值 +',
    valueMinus: '数值 −',
    valuePlusPlus: '数值 ++',
    valueMinusMinus: '数值 −−',
    multiPlus: '数量 +',
    multiMinus: '数量 −',
    multiPlusPlus: '数量 ++',
    multiMinusMinus: '数量 −−',
    roundPlus: '舍入 +',
    roundMinus: '舍入 −',
    resetToTimes1: '重置为 ×1',
    resetQty: '重置数量',
    resetQtyAndRounding: '重置数量与舍入',
    resetRounding: '重置舍入',
    reflectQtyToValue: '将数量反映到数值',
    swapValueAndMulti: '交换数值与数量',
    enableBalancingToCurrentTotal: '按当前合计启用平衡',
    disableBalancing: '禁用平衡',
    switchBalancingToThisPouch: '将平衡切换到此小袋',
    balancingSameAsBpButton: '平衡（与 BP 按钮相同）',
    balancingSameAsThisButton: '平衡（与此按钮相同）',
    longPressDragClonePouch: '复制小袋',
    clonePouchDrag: '复制小袋',
    editGroupTitle: '编辑组名',
    deleteGroup: '删除分组',
    deleteGroupThisFrame: '删除分组（此框）',
    deleteGroupProgressive: '删除分组（渐进）',
    deleteGroupAll: '删除分组（全部）',
    deleteGroupGroups: '删除分组（分组）',
    deleteGroupInnerGroupsAndPouches: '删除分组（内部分组和小袋）',
    deleteGroupInnerGroups: '删除分组（内部分组）',
    dragMoveGroup: '移动分组',
    dragResizeGroup: '调整分组大小',
    dragMoveGroupFrameOnly: '仅移动分组框',
    longPressDragCloneGroup: '复制分组',
    longPressDragCloneGroupFrameOnly: '仅复制分组框',
    cloneGroupDrag: '复制分组',
    cloneGroupFrameOnlyDrag: '仅复制分组框',
    exportSummaryText: '导出摘要文本',
    toggleSignAllPouchesInGroup: '切换分组内所有小袋的 +/-',
    addGroupTotalPouch: '添加分组合计小袋',
    mergeGtpToValues: '将分组合计小袋合并到数值',
    mergeGtpToMultipliers: '将分组合计小袋合并到数量',
    mergeGtpToMultipliersLongPress: '将分组合计小袋合并到数量',
    enableMute: '启用静音',
    switchLayer: '切换图层',
    previousNextLayer: '上一 / 下一图层',
    addLayerEditName: '添加图层（编辑名称）',
    deleteLayerPressTwice: '删除图层（按两次）',
    selectLayer: '选择图层',
    editLayerName: '编辑图层名称',
    commitName: '确认名称',
    nextLayerEditName: '下一图层（编辑名称）',
    previousLayerEditName: '上一图层（编辑名称）',
    addLayer: '添加图层',
    deleteLayer: '删除图层',
    exportWholeTableToExcel: '将整个表格导出到 Excel',
    dragSelectCells: '选择单元格',
    undo: '撤销',
    deleteCurrentHistoryAndUndo: '删除当前历史记录并撤销',
    slideShowReverse: '幻灯片放映（反向）',
    redo: '重做',
    deleteCurrentHistoryAndRedo: '删除当前历史记录并重做',
    slideShowForward: '幻灯片放映（正向）',
    addSnapshot: '添加快照',
    editSnapshotTitle: '编辑快照名称',
    restoreSnapshot: '恢复快照',
    overwriteSnapshot: '覆盖快照',
    deleteSnapshot: '删除快照',
    editColorLabel: '编辑颜色标签',
    doubleClickCreatePouch: '创建小袋',
    dragCreateGroup: '创建分组',
  },
};

export function getTooltipCopy(locale: AppLocale): TooltipCopy {
  return TOOLTIP_COPY[locale];
}

/** tt.clearSelectedCells — English plural only */
export function clearSelectedCells(count: number, locale: AppLocale): string {
  if (locale === 'en') {
    return count > 1 ? 'Clear Selected Cells' : 'Clear Selected Cell';
  }
  const labels: Record<'ja' | 'zh', string> = {
    ja: '選択したセルをクリア',
    zh: '清除所选单元格',
  };
  return labels[locale];
}

export function copySelectedCells(count: number, locale: AppLocale): string {
  if (locale === 'en') {
    return count > 1 ? 'Copy Selected Cells' : 'Copy Selected Cell';
  }
  const labels: Record<'ja' | 'zh', string> = {
    ja: '選択したセルをコピー',
    zh: '复制所选单元格',
  };
  return labels[locale];
}

export function pasteSelectedCells(count: number, locale: AppLocale): string {
  if (locale === 'en') {
    return count > 1 ? 'Paste to Selected Cells' : 'Paste to Selected Cell';
  }
  const labels: Record<'ja' | 'zh', string> = {
    ja: '選択したセルにペースト',
    zh: '粘贴到所选单元格',
  };
  return labels[locale];
}

export function changePouchColor(color: string, locale: AppLocale): string {
  const prefix: Record<AppLocale, string> = {
    en: 'Change Pouch Color to',
    ja: 'パウチの色を次に変更',
    zh: '将小袋颜色更改为',
  };
  return `${prefix[locale]} ${color}`;
}

export function changeGroupColor(color: string, locale: AppLocale): string {
  const prefix: Record<AppLocale, string> = {
    en: 'Change Group Color to',
    ja: 'グループの色を次に変更',
    zh: '将分组颜色更改为',
  };
  return `${prefix[locale]} ${color}`;
}

export function enableMuteSymbol(symbol: string, locale: AppLocale): string {
  const prefix = getTooltipCopy(locale).enableMute;
  return `${prefix} ${symbol}`;
}

export function doubleClickEditColumn(column: string, locale: AppLocale): string {
  if (locale === 'ja') return `${column}を編集`;
  if (locale === 'zh') return `编辑${column}`;
  return `Edit ${column}`;
}

export function doubleClickToggleColumn(column: string, locale: AppLocale): string {
  if (locale === 'ja') return `${column}を切り替え`;
  if (locale === 'zh') return `切换${column}`;
  return `Toggle ${column}`;
}

export function deleteClearColumn(column: string, locale: AppLocale): string {
  if (locale === 'ja') return `${column}をクリア`;
  if (locale === 'zh') return `清除${column}`;
  return `Clear ${column}`;
}

export function enableBalancingToTotal(total: string, locale: AppLocale): string {
  const tt = getTooltipCopy(locale);
  return `${tt.enableBalancingToCurrentTotal} (${total})`;
}

export function switchBalancingToTarget(target: string, locale: AppLocale): string {
  const tt = getTooltipCopy(locale);
  return `${tt.switchBalancingToThisPouch} (Target: ${target})`;
}

export function countLegendLabel(count: string): string {
  return `COUNT: ${count}`;
}

export function commitValue(locale: AppLocale): string {
  const labels: Record<AppLocale, string> = {
    en: 'Commit Value',
    ja: '数値を確定',
    zh: '确认数值',
  };
  return labels[locale];
}
